/*
    Bubble chart module
*/
import cloneDeep from 'lodash/cloneDeep';
import bubbleChartConfigBuilder from './bubble-chart-config-builder';
import { bubbleTooltip } from '../widget-utilities/widget-tooltip';
import d3Utils from '../../../utils/d3Utils';
import chartUtils from '../../../utils/chart-utils';

var bubbleChart = function bubbleChart() {

    // Render column chart widget
    var renderWidget = function renderWidget(widgetConfig) {
        var ContainerId = widgetConfig.ContainerId,
            Model = widgetConfig.Model,
            Width = widgetConfig.Width,
            Height = widgetConfig.Height,
            Cloud = widgetConfig.Cloud,
            Data = widgetConfig.Data,
            BubbleChartConfig = widgetConfig.BubbleChartConfig;


        var parentContainerId = ContainerId.includes('flip-') ? ContainerId.split('flip-')[1] : ContainerId;

        var el = window.document.querySelector('#' + ContainerId);

        d3.select(el).select('svg').remove();

        var modelName = Model;
        var data = JSON.parse(chartUtils.replaceAll(JSON.stringify(Data.data), '\x01', ""));
        var width = Width;
        var height = Height;
        var displayFormat = BubbleChartConfig.ValueFormat;
        var currencyPrefix = BubbleChartConfig.CurrencySymbol;
        var baseFormat = BubbleChartConfig.BaseFormat;
        var accumulationType = BubbleChartConfig.AggregationType;
        var CrumbFieldConfig = BubbleChartConfig.CrumbFieldConfig;
        var crumbFieldName = CrumbFieldConfig[Object.keys(CrumbFieldConfig)[0]];
        var crumbDisplayFieldName = BubbleChartConfig.CrumbDisplayFieldName;
        var crumbFormat = {
            'CloudName': Cloud,
            'ModelName': modelName,
            'Type': 'Node',
            'Name': Object.keys(CrumbFieldConfig)[0],
            'FieldName': crumbFieldName,
            'Value': '',
            'DisplayFieldName': crumbDisplayFieldName,
            'DisplayValue': null
        };
        var displayCount = BubbleChartConfig.DisplayCount;
        var inverseSort = BubbleChartConfig.InverseSort;
        var showValue = BubbleChartConfig.ShowLabels;
        var tipField = BubbleChartConfig.TipField;
        var tooltipConfig = BubbleChartConfig.CompoundTipField;
        var totalRecords = BubbleChartConfig.TotalRecords;
        var showDefaultTooltip = BubbleChartConfig.ShowDefaultTooltip;
        var defaultPerspective = BubbleChartConfig.DefaultPerspective;

        var nameField = BubbleChartConfig.AreaNameField;
        var valueField = BubbleChartConfig.AreaValueField;

        // Handle the negative values
        data = data.filter(function (prop) {
            return prop[prop["valueFieldName"]] > 0;
        });

        if (isNaN(displayCount)) displayCount = 0;

        var format = d3.format(",d"),
            color = d3.scale.category20c();

        var bubble = d3.layout.pack().sort(null).size([width, height]).padding(1.5);

        var svg = d3.select(el).append("svg").attr("width", width).attr("height", height).attr("class", "bubble-chart maximize-trigger").attr('viewBox', '0 0 ' + width + ' ' + height).attr('preserveAspectRatio', 'xMidYMid');

        var node = null;

        if (tooltipConfig != null && tooltipConfig.toString().trim().length > 0) tooltipConfig = JSON.parse(chartUtils.replaceAll(JSON.stringify(tooltipConfig), '\x01', ''));

        node = svg.selectAll(".bubble").data(bubble.nodes(classes(data)).filter(function (d) {
            return !d.children;
        })).enter().append("g").attr("class", "bubble").attr("transform", function (d) {
            return "translate(" + d.x + "," + d.y + ")";
        }).on("touchstart", function () {
            bubbleTooltip.onMouseOver.call(this, parentContainerId);
        }).on("mouseover", function () {
            bubbleTooltip.onMouseOver.call(this, parentContainerId);
        }).on("mouseout", bubbleTooltip.onMouseOut).on("touchmove", bubbleTooltip.onTouchMove).on("mousemove", function () {
            bubbleTooltip.onMouseMove.call(this, parentContainerId);
        });

        node.append("circle").attr("r", 0).attr("label", function (d) {
            if (showDefaultTooltip) {
                var defaultTip = '<span class="tip-header">' + d.tip + ':</span><span class="tip-value"> ' + chartUtils.format(d.value, displayFormat, currencyPrefix, null, baseFormat) + '</span>';
            }

            var tipText = chartUtils.buildTooltipText(tooltipConfig, d, defaultTip, valueField, accumulationType, totalRecords);

            return tipText;
        }).attr("class", function (d, i) {
            return "bubble-circle dataviz circ" + i;
        }).attr("id", function (d, i) {
            return "circ" + i;
        }).attr("coord", function (d) {
            crumbFormat.Value = d["CrumbValue"];
            crumbFormat.DisplayValue = d["CrumbDisplayValue"];
            crumbFormat.DefaultPerspective = defaultPerspective;
            return JSON.stringify(crumbFormat);
        }).style("fill", function (d) {
            return color(d.packageName);
        }).transition().duration(1000).delay(function (d, i) {
            return i * 5;
        }).ease("bounce").attr('r', function (d, i) {
            return d.r;
        });

        node.append("text").attr("dy", ".3em").attr("label", function (d) {
            if (showDefaultTooltip) {
                var defaultTip = '<span class="tip-header">' + d.tip + ':</span><span class="tip-value"> ' + chartUtils.format(d.value, displayFormat, currencyPrefix, null, baseFormat) + '</span>';
            }

            var tipText = chartUtils.buildTooltipText(tooltipConfig, d, defaultTip, valueField, accumulationType, totalRecords);

            return tipText;
        }).attr("class", "bubble-label").attr("opacity", 0).transition().delay(function (d, i) {
            return i * 5;
        }).duration(200).attr("opacity", 1).style("text-anchor", "middle").text(function (d) {
            if (showValue) {
                if (d.value == null) return "--";

                var value = chartUtils.format(d.value, displayFormat, currencyPrefix, null, baseFormat);
                if (value != null) return value.toString().substring(0, Math.round(d.r / 5));
            } else {
                if (d.className == null) return "--";

                return d.className.toString().substring(0, Math.round(d.r / 5));
            }
        });

        // Returns a flattened hierarchy containing all leaf nodes under the root.
        function classes(data) {
            var classes = [];

            chartUtils.sort(data, valueField, null, inverseSort);

            if (displayCount > 0) data = data.slice(0, displayCount);

            var maxValue = d3.max(data, function (d) {
                return Number(d[valueField]);
            });

            var minValue = d3.min(data, function (d) {
                return Number(d[valueField]);
            });

            for (var i = 0; i < data.length; i++) {
                var dt = data[i];

                if (classes.map(function (d) {
                    return d["packageName"];
                }).indexOf(dt[nameField]) > -1) continue;

                var tip = dt[tipField] == null ? dt["valueFieldName"] : dt[tipField] + '<br/>' + dt["valueFieldName"];

                var item = {
                    packageName: dt[nameField],
                    className: dt[nameField],
                    value: dt[valueField],
                    "CrumbValue": dt[crumbFieldName],
                    "CrumbDisplayValue": dt[crumbDisplayFieldName],
                    tip: tip,
                    "counter": dt.counter,
                    "total": dt.total,
                    tipField: dt[tipField],
                    valueFieldName: dt["valueFieldName"]
                };

                var tooltips = dt["tooltip"];

                if (tooltips != null && tooltipConfig != null && tooltipConfig.TooltipConfigs != null && tooltipConfig.TooltipConfigs.length > 0) for (var index = 0; index < tooltipConfig.TooltipConfigs.length; index++) {
                    item[tooltipConfig.TooltipConfigs[index].Id] = tooltips[tooltipConfig.TooltipConfigs[index].Id];
                }classes.push(item);
            }

            return {
                children: classes
            };
        }
    };

    var initiateWidgetRendering = function initiateWidgetRendering(configObj) {
        // Get widget specific widgetConfig
        var widgetConfig = bubbleChartConfigBuilder.configBuilder(cloneDeep(configObj));
        renderWidget(widgetConfig);
    };
    return {
        renderWidget: renderWidget,
        initiateWidgetRendering: initiateWidgetRendering
    };
};

export default bubbleChart();